import { useState, type FormEvent } from 'react';
import { siteStats } from '@/mocks/homeData';

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dahgcijp14a1h3mr6lug';
const MESSAGE_LIMIT = 500;

const contactInfo = [
  { icon: 'ri-mail-line', label: '投稿与联络', value: 'hello@meirichigua.com' },
  { icon: 'ri-time-line', label: '更新时段', value: '每天 09:00 / 21:00' },
  { icon: 'ri-chat-smile-2-line', label: '纠错反馈', value: '表单提交后 48 小时内回复' },
];

export default function SubscriptionSection() {
  const [messageLen, setMessageLen] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setFeedback({ type: 'success', message: '提交成功，感谢你的来信，我们会尽快处理。' });
      form.reset();
      setMessageLen(0);
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      if (typeof value === 'string' && value.trim() === '') return;
      params.append(key, value);
    });

    setSubmitting(true);
    setFeedback(null);

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setFeedback({ type: 'success', message: '提交成功，我们会在每周合集里优先回复你的留言。' });
        form.reset();
        setMessageLen(0);
      } else {
        setFeedback({
          type: 'error',
          message: serverMsg ? `提交未成功：${serverMsg}` : '提交未成功，请稍后再试。',
        });
      }
    } catch {
      setFeedback({ type: 'error', message: '网络异常，提交未成功，请检查网络后重试。' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="subscription" className="scroll-mt-24 bg-background-100 py-16 md:py-24">
      <div className="mx-auto px-4 md:px-10">
        <div className="overflow-hidden rounded-lg border border-foreground-950/10 bg-background-50">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            <div className="bg-secondary-900 p-7 text-background-100 md:p-10 lg:col-span-5">
              <span className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.3em] text-accent-400">
                <span className="h-px w-6 bg-accent-400" />
                SUBSCRIBE
              </span>
              <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-background-50 md:text-3xl">
                订阅更新，或投来你身边的怪事
              </h2>
              <p className="mt-4 text-sm leading-8 text-background-200">
                填写下方表单即可加入「每周吃瓜合集」，也可以把你知道的奇闻投给编辑组。
                我们只采纳能说清时间、地点的线索。
              </p>

              <ul className="mt-8 space-y-4">
                {contactInfo.map((item) => (
                  <li key={item.label} className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-800 text-lg text-accent-400">
                      <i className={item.icon}></i>
                    </span>
                    <span className="flex flex-col">
                      <span className="text-xs text-background-300">{item.label}</span>
                      <span className="mt-0.5 text-sm text-background-50">{item.value}</span>
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 border-t border-background-100/15 pt-6">
                {siteStats.slice(0, 2).map((s) => (
                  <div key={s.label}>
                    <p className="font-heading text-xl font-bold text-background-50">{s.value}</p>
                    <p className="text-xs text-background-300">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-7 md:p-10 lg:col-span-7">
              <form onSubmit={handleSubmit} data-readdy-form="dahgcijp14a1h3mr6lug" id="subscribe-form" noValidate>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="nickname" className="text-sm font-medium text-foreground-800">
                      怎么称呼你
                    </label>
                    <input
                      id="nickname"
                      name="nickname"
                      type="text"
                      required
                      maxLength={30}
                      placeholder="例如：阿凉"
                      className="w-full rounded-md border border-foreground-950/15 bg-background-50 px-4 py-3 text-sm text-foreground-900 transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="email" className="text-sm font-medium text-foreground-800">
                      邮箱
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="w-full rounded-md border border-foreground-950/15 bg-background-50 px-4 py-3 text-sm text-foreground-900 transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>

                <div className="mt-5 flex flex-col gap-2">
                  <label htmlFor="intent" className="text-sm font-medium text-foreground-800">
                    你想做什么
                  </label>
                  <select
                    id="intent"
                    name="intent"
                    defaultValue="订阅每周合集"
                    className="w-full rounded-md border border-foreground-950/15 bg-background-50 px-4 py-3 text-sm text-foreground-900 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  >
                    <option value="订阅每周合集">订阅每周合集</option>
                    <option value="投稿一则奇闻">投稿一则奇闻</option>
                    <option value="内容纠错">内容纠错</option>
                    <option value="商务与媒体合作">商务与媒体合作</option>
                  </select>
                </div>

                <div className="mt-5 flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <label htmlFor="message" className="text-sm font-medium text-foreground-800">
                      想说的话（选填）
                    </label>
                    <span className={`text-xs ${messageLen > MESSAGE_LIMIT ? 'text-primary-600' : 'text-foreground-400'}`}>
                      {messageLen}/{MESSAGE_LIMIT}
                    </span>
                  </div>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={MESSAGE_LIMIT}
                    onChange={(e) => setMessageLen(e.target.value.length)}
                    placeholder="如果是投稿，请写清楚时间、地点和你能确认的部分。"
                    className="w-full resize-none rounded-md border border-foreground-950/15 bg-background-50 px-4 py-3 text-sm leading-7 text-foreground-900 transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="subscribe-verify-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div className="mt-7 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <i className={submitting ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-fill'}></i>
                    {submitting ? '提交中…' : '提交'}
                  </button>
                  <p className="text-xs leading-6 text-foreground-500">
                    提交即表示你同意我们使用该邮箱发送更新，可随时退订。
                  </p>
                </div>

                {feedback && (
                  <div
                    role="status"
                    className={`mt-5 flex items-start gap-3 rounded-md border px-4 py-3 text-sm ${
                      feedback.type === 'success'
                        ? 'border-secondary-300 bg-secondary-50 text-secondary-900'
                        : 'border-primary-300 bg-primary-50 text-primary-800'
                    }`}
                  >
                    <i
                      className={
                        feedback.type === 'success' ? 'ri-checkbox-circle-line mt-0.5' : 'ri-error-warning-line mt-0.5'
                      }
                    ></i>
                    <span className="leading-6">{feedback.message}</span>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}